if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.visibilityMode = this.dataset.visibility || 'always';

        this.variantPicker =
          document.querySelector(`variant-radios[data-section="${this.sectionId}"]`) ||
          document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
        this.mainSubmitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);

        this.submitButton = this.querySelector('[data-sticky-atc-submit]');
        this.submitText = this.querySelector('[data-sticky-atc-submit-text]');
        this.spinner = this.querySelector('[data-sticky-atc-spinner]');
        this.priceContainer = this.querySelector('[data-sticky-atc-price]');
        this.variantData = this.getVariantData();

        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onVariantPickerChange = this.onVariantPickerChange.bind(this);
        this.updateHeight = this.updateHeight.bind(this);

        this.bindOptions();
        this.bindSubmit();
        this.watchMainSubmitButton();
        this.watchVariantChanges();
        this.setupVisibility();
        this.syncOptions();

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('resize', this.updateHeight);

        if ('ResizeObserver' in window) {
          this.heightObserver = new ResizeObserver(this.updateHeight);
          this.heightObserver.observe(this);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('resize', this.updateHeight);

        if (this.variantPicker) this.variantPicker.removeEventListener('change', this.onVariantPickerChange);
        if (this.submitObserver) this.submitObserver.disconnect();
        if (this.visibilityObserver) this.visibilityObserver.disconnect();
        if (this.heightObserver) this.heightObserver.disconnect();
        if (this.unsubscribeVariantChange) this.unsubscribeVariantChange();

        document.body.classList.remove('sticky-atc-active');
      }

      getVariantData() {
        const script = this.querySelector('[data-sticky-atc-variants]');
        if (!script) return [];

        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          return [];
        }
      }

      /* Visibility */

      setupVisibility() {
        if (this.visibilityMode !== 'scroll' || !this.mainSubmitButton) {
          this.show();
          return;
        }

        const anchor = this.mainSubmitButton.closest('.product-form__buttons') || this.mainSubmitButton;

        this.visibilityObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const scrolledPast = entry.boundingClientRect.top < 0;
            if (!entry.isIntersecting && scrolledPast) {
              this.show();
            } else {
              this.hide();
            }
          });
        });

        this.visibilityObserver.observe(anchor);
      }

      show() {
        this.classList.add('is-visible');
        document.body.classList.add('sticky-atc-active');
        this.updateHeight();
      }

      hide() {
        this.closeOptions();
        this.classList.remove('is-visible');
        document.body.classList.remove('sticky-atc-active');
      }

      updateHeight() {
        document.documentElement.style.setProperty('--sticky-atc-height', `${this.offsetHeight}px`);
      }

      /* Option selectors */

      bindOptions() {
        this.querySelectorAll('[data-sticky-atc-option-toggle]').forEach((toggle) => {
          toggle.addEventListener('click', (event) => {
            event.preventDefault();
            this.toggleOption(toggle.closest('[data-sticky-atc-option]'));
          });
        });

        this.querySelectorAll('[data-sticky-atc-value]').forEach((value) => {
          value.addEventListener('click', () => this.selectValue(value));
        });
      }

      toggleOption(option) {
        if (!option) return;

        const isOpen = option.classList.contains('is-open');
        this.closeOptions();
        if (isOpen) return;

        const toggle = option.querySelector('[data-sticky-atc-option-toggle]');
        const popover = option.querySelector('[data-sticky-atc-popover]');

        option.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
        popover.removeAttribute('hidden');
        this.keepPopoverInViewport(popover);
      }

      closeOptions() {
        this.querySelectorAll('[data-sticky-atc-option].is-open').forEach((option) => {
          option.classList.remove('is-open');
          option.querySelector('[data-sticky-atc-option-toggle]').setAttribute('aria-expanded', 'false');

          const popover = option.querySelector('[data-sticky-atc-popover]');
          popover.setAttribute('hidden', '');
          popover.classList.remove('sticky-atc__popover--align-left', 'sticky-atc__popover--align-right');
        });
      }

      keepPopoverInViewport(popover) {
        const gutter = 16;
        const rect = popover.getBoundingClientRect();

        if (rect.right > window.innerWidth - gutter) {
          popover.classList.add('sticky-atc__popover--align-right');
        } else if (rect.left < gutter) {
          popover.classList.add('sticky-atc__popover--align-left');
        }
      }

      selectValue(valueButton) {
        const position = Number(valueButton.dataset.optionPosition);
        const value = valueButton.dataset.optionValue;
        const input = this.findOptionInput(position, value);

        this.closeOptions();
        if (!input) return;

        if (input.tagName === 'SELECT') {
          if (input.value === value) return;
          input.value = value;
        } else {
          if (input.checked) return;
          input.checked = true;
        }

        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      getOptionInputs(position) {
        if (!this.variantPicker) return [];

        const fieldsets = this.variantPicker.querySelectorAll('fieldset.product-form__input');
        if (fieldsets.length >= position) {
          return Array.from(fieldsets[position - 1].querySelectorAll('input[type="radio"]'));
        }

        const selects = this.variantPicker.querySelectorAll('select');
        return selects.length >= position ? [selects[position - 1]] : [];
      }

      findOptionInput(position, value) {
        const inputs = this.getOptionInputs(position);
        if (inputs.length === 1 && inputs[0].tagName === 'SELECT') return inputs[0];

        return inputs.find((input) => input.value === value) || null;
      }

      getSelectedValue(position) {
        const inputs = this.getOptionInputs(position);
        if (!inputs.length) return null;
        if (inputs[0].tagName === 'SELECT') return inputs[0].value;

        const checked = inputs.find((input) => input.checked);
        return checked ? checked.value : null;
      }

      syncOptions() {
        this.querySelectorAll('[data-sticky-atc-option]').forEach((option) => {
          const position = Number(option.dataset.optionPosition);
          const selectedValue = this.getSelectedValue(position);
          const selectedLabel = option.querySelector('[data-sticky-atc-selected-value]');
          const swatchTarget = option.querySelector('[data-sticky-atc-swatch]');

          if (selectedLabel && selectedValue) selectedLabel.textContent = selectedValue;

          option.querySelectorAll('[data-sticky-atc-value]').forEach((valueButton) => {
            const value = valueButton.dataset.optionValue;
            const isSelected = value === selectedValue;
            const input = this.findOptionInput(position, value);

            valueButton.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
            valueButton.classList.toggle(
              'is-unavailable',
              Boolean(input) && input.tagName !== 'SELECT' && input.classList.contains('disabled')
            );

            if (isSelected && swatchTarget && valueButton.dataset.swatch) {
              swatchTarget.style.background = valueButton.dataset.swatch;
            }
          });
        });
      }

      /* Price and button state */

      onVariantPickerChange() {
        this.syncOptions();
      }

      watchVariantChanges() {
        if (this.variantPicker) {
          this.variantPicker.addEventListener('change', this.onVariantPickerChange);
        }

        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!data || data.sectionId !== this.sectionId) return;
          this.updatePrice(data.variant);
          this.syncOptions();
        });
      }

      updatePrice(variant) {
        if (!variant || !this.priceContainer) return;

        const prices = this.variantData.find((item) => item.id === variant.id);
        if (!prices) return;

        this.priceContainer.innerHTML = '';

        const current = document.createElement('span');
        current.className = 'sticky-atc__price-current';
        current.textContent = prices.price;
        this.priceContainer.appendChild(current);

        if (prices.on_sale && prices.compare_at_price) {
          const compare = document.createElement('s');
          compare.className = 'sticky-atc__price-compare';
          compare.textContent = prices.compare_at_price;
          this.priceContainer.appendChild(compare);
        }
      }

      watchMainSubmitButton() {
        if (!this.mainSubmitButton) return;

        this.submitObserver = new MutationObserver(() => this.syncSubmitButton());
        this.submitObserver.observe(this.mainSubmitButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true,
        });

        this.syncSubmitButton();
      }

      syncSubmitButton() {
        if (!this.mainSubmitButton || !this.submitButton) return;

        const isLoading = this.mainSubmitButton.classList.contains('loading');

        this.submitButton.disabled = this.mainSubmitButton.hasAttribute('disabled');
        this.submitButton.classList.toggle('loading', isLoading);
        if (this.spinner) this.spinner.classList.toggle('hidden', !isLoading);

        const mainText = this.mainSubmitButton.querySelector('span');
        if (mainText && this.submitText) this.submitText.textContent = mainText.textContent.trim();
      }

      bindSubmit() {
        if (!this.submitButton) return;

        this.submitButton.addEventListener('click', () => {
          if (!this.mainSubmitButton || this.submitButton.disabled) return;
          this.mainSubmitButton.click();
        });
      }

      /* Global interactions */

      onDocumentClick(event) {
        if (this.contains(event.target)) return;
        this.closeOptions();
      }

      onKeyDown(event) {
        if (event.key !== 'Escape') return;
        this.closeOptions();
      }
    }
  );
}
